import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import propertyCheckService from '../services/propertyCheckService';

const toEditable = (pkg) => ({
  displayName: pkg.displayName || '',
  description: pkg.description || '',
  basePriceUsd: pkg.basePriceUsd ?? 0,
  turnaroundHours: pkg.turnaroundHours ?? 24,
  scopeTemplateId: pkg.scopeTemplateId || '',
  isUrgent: !!pkg.isUrgent,
  availableCitiesText: (pkg.availableCities || []).join(', '),
  sortOrder: pkg.sortOrder ?? 0,
  isActive: !!pkg.isActive,
});

const AdminPropertyCheckPackages = () => {
  const [packages, setPackages] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await propertyCheckService.listAdminPackages();
      setPackages(rows);
      setDrafts(Object.fromEntries(rows.map((pkg) => [pkg.slug, toEditable(pkg)])));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateDraft = (slug, patch) => {
    setDrafts((prev) => ({ ...prev, [slug]: { ...prev[slug], ...patch } }));
  };

  const save = async (pkg) => {
    const draft = drafts[pkg.slug];
    setSavingSlug(pkg.slug);
    setError(null);
    setSuccess(null);
    try {
      const updated = await propertyCheckService.updateAdminPackage(pkg.slug, {
        displayName: draft.displayName,
        description: draft.description,
        basePriceUsd: Number(draft.basePriceUsd),
        turnaroundHours: Number(draft.turnaroundHours),
        scopeTemplateId: draft.scopeTemplateId || null,
        isUrgent: draft.isUrgent,
        availableCities: draft.availableCitiesText
          .split(',')
          .map((city) => city.trim())
          .filter(Boolean),
        sortOrder: Number(draft.sortOrder),
        isActive: draft.isActive,
      });
      setPackages((prev) => prev.map((p) => (p.slug === updated.slug ? updated : p)));
      setDrafts((prev) => ({ ...prev, [updated.slug]: toEditable(updated) }));
      setSuccess(`${updated.displayName} saved`);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to save package');
    } finally {
      setSavingSlug(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Property Check Packages
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage service pricing, turnaround promises, city restrictions, and package visibility.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {packages.filter((p) => (drafts[p.slug] || p).isActive).length} of {packages.length} packages
          live on the booking page. Inactive packages stay listed here so they can be re-enabled.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Grid container spacing={2.5}>
        {packages.map((pkg) => {
          const draft = drafts[pkg.slug] || toEditable(pkg);
          return (
            <Grid item xs={12} md={6} key={pkg.slug}>
              <Card variant="outlined" sx={{ height: '100%', opacity: draft.isActive ? 1 : 0.65 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        {pkg.slug}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {pkg.displayName}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {draft.isUrgent && <Chip size="small" color="warning" label="Urgent" />}
                      <Chip
                        size="small"
                        color={draft.isActive ? 'success' : 'default'}
                        label={draft.isActive ? 'Live' : 'Hidden from customers'}
                      />
                      <Switch
                        checked={draft.isActive}
                        onChange={(e) => updateDraft(pkg.slug, { isActive: e.target.checked })}
                      />
                    </Stack>
                  </Stack>

                  <Stack spacing={2}>
                    <TextField
                      label="Display name"
                      value={draft.displayName}
                      onChange={(e) => updateDraft(pkg.slug, { displayName: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      label="Description"
                      value={draft.description}
                      onChange={(e) => updateDraft(pkg.slug, { description: e.target.value })}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Price USD"
                        type="number"
                        value={draft.basePriceUsd}
                        onChange={(e) => updateDraft(pkg.slug, { basePriceUsd: e.target.value })}
                        fullWidth
                      />
                      <TextField
                        label="Turnaround hours"
                        type="number"
                        value={draft.turnaroundHours}
                        onChange={(e) => updateDraft(pkg.slug, { turnaroundHours: e.target.value })}
                        fullWidth
                      />
                    </Stack>
                    <TextField
                      label="Available cities"
                      helperText="Comma-separated. Leave blank for all serviced cities."
                      value={draft.availableCitiesText}
                      onChange={(e) => updateDraft(pkg.slug, { availableCitiesText: e.target.value })}
                      fullWidth
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Scope template"
                        value={draft.scopeTemplateId}
                        onChange={(e) => updateDraft(pkg.slug, { scopeTemplateId: e.target.value })}
                        fullWidth
                      />
                      <TextField
                        label="Sort order"
                        type="number"
                        value={draft.sortOrder}
                        onChange={(e) => updateDraft(pkg.slug, { sortOrder: e.target.value })}
                        fullWidth
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">Urgent package</Typography>
                        <Switch
                          checked={draft.isUrgent}
                          onChange={(e) => updateDraft(pkg.slug, { isUrgent: e.target.checked })}
                        />
                      </Stack>
                      <Button
                        variant="contained"
                        startIcon={savingSlug === pkg.slug ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        disabled={savingSlug === pkg.slug}
                        onClick={() => save(pkg)}
                      >
                        Save
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default AdminPropertyCheckPackages;
